import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import EventRow from './EventRow';
import { placeBet, cancelMatchBet } from '../../services/api';
import pointsIcon from '../../assets/points_icon.png';

const formatMatchTime = (startTimeISO) => {
    const matchDate = new Date(startTimeISO);
    const now = new Date();
    const isToday = matchDate.getDate() === now.getDate() && matchDate.getMonth() === now.getMonth();
    const tomorrow = new Date();
    tomorrow.setDate(now.getDate() + 1);
    const isTomorrow = matchDate.getDate() === tomorrow.getDate() && matchDate.getMonth() === tomorrow.getMonth();

    const time = matchDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (isToday) return `Сегодня в ${time}`;
    if (isTomorrow) return `Завтра в ${time}`;
    return `${matchDate.getDate().toString().padStart(2, '0')}.${(matchDate.getMonth() + 1).toString().padStart(2, '0')} в ${time}`;
};

export default function MatchRow({ match, onBetSuccess }) {
    const [showBetModal, setShowBetModal] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [amount, setAmount] = useState('10');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [eventsOpen, setEventsOpen] = useState(false);

    const isThreeWay = !!match.team3Id;
    const userBet = match.bets && match.bets[0];
    const isMatchLocked = !!match.winnerId || new Date() >= new Date(match.startTime);

    const openBetModal = (team) => {
        if (isMatchLocked) return;
        if (userBet && userBet.teamId !== team.id) return;

        setSelectedTeam(team);
        setAmount('10'); // Сбрасываем на дефолтную сумму добавления
        setShowBetModal(true);
        setMessage('');
    };

    const handlePlaceBet = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        try {
            await placeBet({
                matchId: match.id,
                teamId: selectedTeam.id,
                amount: parseInt(amount, 10)
            });
            setMessage('Ставка принята!');
            setTimeout(() => {
                setShowBetModal(false);
                onBetSuccess();
            }, 1000);
        } catch (error) {
            setMessage(error.response?.data?.error || 'Ошибка');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelBet = async () => {
        if (!window.confirm('Вы уверены, что хотите отменить ставку? Очки вернутся на баланс.')) return;
        setIsLoading(true);
        try {
            await cancelMatchBet(match.id);
            setMessage('Ставка отменена!');
            setTimeout(() => {
                setShowBetModal(false);
                onBetSuccess();
            }, 1000);
        } catch (err) {
            setMessage(err.response?.data?.error || 'Ошибка отмены');
        } finally {
            setIsLoading(false);
        }
    };

    const getSelectedOdds = () => {
        if (!selectedTeam) return 1;
        if (isThreeWay) {
            if (selectedTeam.id === match.team1Id) return match.odds1;
            if (selectedTeam.id === match.team2Id) return match.odds2;
            return match.odds3;
        }
        return selectedTeam.id === match.team1Id ? match.odds1 : match.odds2;
    };

    const odds = getSelectedOdds();
    const currentBetAmount = userBet ? userBet.amount : 0;
    const addedAmount = parseInt(amount, 10) || 0;
    const totalBetAmount = currentBetAmount + addedAmount;
    const potentialWin = Math.floor(totalBetAmount * (odds || 1));
    const profit = potentialWin - totalBetAmount;

    // Рендер кнопок ставок (П1 / П2 / П3)
    const renderBetTile = (team, teamId, tileOdds, label) => {
        if (!team) return null;
        const isChosen = userBet && userBet.teamId === teamId;
        const isOther = userBet && userBet.teamId !== teamId;
        const isDisabled = isMatchLocked || isOther;

        // Если матч завершен — подсвечиваем исход ставки пользователя (зеленый/красный)
        if (match.winnerId) {
            const isWinner = match.winnerId === teamId;
            let resultClass = '';
            if (isChosen) {
                resultClass = isWinner ? 'user-won' : 'user-lost';
            }

            return (
                <div className={`match-bet-tile is-finished ${resultClass}`}>
                    <span className="tile-label">{label}</span>
                    <span className="tile-val">
                        {isChosen ? (
                            isWinner ? (
                                `+${Math.floor(userBet.amount * tileOdds) - userBet.amount}`
                            ) : (
                                `-${userBet.amount}`
                            )
                        ) : (
                            tileOdds ? tileOdds.toFixed(2) : '-'
                        )}
                    </span>
                </div>
            );
        }

        return (
            <button
                type="button"
                className={`match-bet-tile ${isChosen ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                onClick={() => openBetModal(team)}
                disabled={isDisabled}
            >
                <span className="tile-label">{label}</span>
                <span className="tile-val">
                    {isChosen ? (
                        <span className="active-bet-amount">
                            {userBet.amount}
                            <img src={pointsIcon} alt="" className="points-icon-small" />
                        </span>
                    ) : (
                        tileOdds ? tileOdds.toFixed(2) : '-'
                    )}
                </span>
            </button>
        );
    };

    // Рендер кликабельного названия команды
    const renderTeamItem = (team, teamId) => {
        const isWon = match.winnerId === teamId;
        const content = (
            <>
                {team?.logoUrl ? (
                    <img src={team.logoUrl} alt="" className="team-row-logo" />
                ) : (
                    <div className="team-row-logo-placeholder">{team?.name?.charAt(0)}</div>
                )}
                <span className="team-row-name">{team?.name}</span>
            </>
        );

        return (
            <div className={`team-row-item ${isWon ? 'winner' : ''}`}>
                {team?.profileUrl ? (
                    <a href={team.profileUrl} target="_blank" rel="noopener noreferrer" className="team-link-wrapper">
                        {content}
                    </a>
                ) : (
                    <div className="team-link-wrapper">
                        {content}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="match-card-wrapper">
            <div className="match-grid-row">
                {/* Левая колонка: Команды и время со ссылкой на матч */}
                <div className="match-teams-col">
                    {renderTeamItem(match.team1, match.team1Id)}
                    {renderTeamItem(match.team2, match.team2Id)}
                    {isThreeWay && match.team3 && renderTeamItem(match.team3, match.team3Id)}

                    <div className="match-sub-info">
                        {match.matchUrl ? (
                            <a
                                href={match.matchUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="match-date-link"
                            >
                                {formatMatchTime(match.startTime)} {match.winnerId ? '(Завершен)' : (isMatchLocked ? '(Идёт)' : '')}
                            </a>
                        ) : (
                            <span className="match-date-label">
                                {formatMatchTime(match.startTime)} {match.winnerId ? '(Завершен)' : (isMatchLocked ? '(Идёт)' : '')}
                            </span>
                        )}
                    </div>
                </div>

                {/* Правая колонка: Плитки ставок П1, П2, П3 */}
                <div className={`match-tiles-col ${isThreeWay ? 'three-way' : ''}`}>
                    {renderBetTile(match.team1, match.team1Id, match.odds1, 'П1')}
                    {renderBetTile(match.team2, match.team2Id, match.odds2, 'П2')}
                    {isThreeWay && renderBetTile(match.team3, match.team3Id, match.odds3, 'П3')}
                </div>
            </div>

            {/* Подвал карточки: кнопка событий на всю ширину с выпадающим списком */}
            {match.events && match.events.length > 0 && (
                <div className="match-events-footer">
                    <button
                        type="button"
                        className="toggle-events-btn-web"
                        onClick={() => setEventsOpen(!eventsOpen)}
                    >
                        {eventsOpen ? 'Скрыть события' : `Дополнительные события (${match.events.length})`}
                        <span className="events-arrow">{eventsOpen ? ' ▲' : ' ▼'}</span>
                    </button>

                    {eventsOpen && (
                        <div className="events-list-web">
                            {match.events.map(ev => (
                                <EventRow key={ev.id} event={ev} isMatchLocked={isMatchLocked} onRefresh={onBetSuccess} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Всплывающее окно ставки через React Portal */}
            {showBetModal && createPortal(
                <div className="modal-overlay" onClick={() => setShowBetModal(false)}>
                    <div className="modal-content modal-size-narrow" onClick={e => e.stopPropagation()}>
                        <button className="modal-close-button" onClick={() => setShowBetModal(false)}>✕</button>

                        <div className="bet-modal-header">
                            <h3>{userBet ? 'Добавить к ставке' : 'Оформление ставки'}</h3>
                        </div>

                        {/* Дуэль команд с логотипами (поддерживает 2 или 3 команды) */}
                        <div className="bet-modal-teams-vs">
                            <div className={`vs-team-box ${selectedTeam?.id === match.team1?.id ? 'selected' : ''}`}>
                                {match.team1?.logoUrl ? (
                                    <img src={match.team1.logoUrl} alt="" className="vs-logo" />
                                ) : (
                                    <div className="vs-placeholder">{match.team1?.name?.charAt(0)}</div>
                                )}
                                <span className="vs-name">{match.team1?.name}</span>
                            </div>

                            <span className="vs-divider">VS</span>

                            <div className={`vs-team-box ${selectedTeam?.id === match.team2?.id ? 'selected' : ''}`}>
                                {match.team2?.logoUrl ? (
                                    <img src={match.team2.logoUrl} alt="" className="vs-logo" />
                                ) : (
                                    <div className="vs-placeholder">{match.team2?.name?.charAt(0)}</div>
                                )}
                                <span className="vs-name">{match.team2?.name}</span>
                            </div>

                            {isThreeWay && match.team3 && (
                                <>
                                    <span className="vs-divider">VS</span>
                                    <div className={`vs-team-box ${selectedTeam?.id === match.team3?.id ? 'selected' : ''}`}>
                                        {match.team3?.logoUrl ? (
                                            <img src={match.team3.logoUrl} alt="" className="vs-logo" />
                                        ) : (
                                            <div className="vs-placeholder">{match.team3?.name?.charAt(0)}</div>
                                        )}
                                        <span className="vs-name">{match.team3?.name}</span>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="bet-pick-highlight">
                            <span>Победа: <strong>{selectedTeam?.name}</strong></span>
                            <span className="odds-badge">x{odds ? odds.toFixed(2) : '-'}</span>
                        </div>

                        {userBet && (
                            <div className="already-bet-alert">
                                <span>Уже в игре: <strong>{userBet.amount}</strong></span>
                                <img src={pointsIcon} alt="pts" className="points-icon-small" />
                            </div>
                        )}

                        <form onSubmit={handlePlaceBet} className="transfer-form">
                            <label>{userBet ? 'Сколько добавить:' : 'Сумма ставки:'}</label>
                            <div className="amount-input-wrapper">
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={e => setAmount(e.target.value)}
                                    min="1"
                                    required
                                    autoFocus
                                />
                                <img src={pointsIcon} alt="points" className="points-icon" />
                            </div>

                            <div className="bet-calc-preview">
                                {userBet && (
                                    <div style={{ marginBottom: 6 }}>
                                        <span className="calc-label">Итоговая ставка:</span>
                                        <span className="calc-val">{totalBetAmount} pts</span>
                                    </div>
                                )}
                                <div>
                                    <span className="calc-label">Возможный выигрыш:</span>
                                    <span className="calc-val">{potentialWin} pts</span>
                                </div>
                                <div>
                                    <span className="calc-label">Чистая прибыль:</span>
                                    <span className="calc-val profit">+{profit > 0 ? profit : 0} pts</span>
                                </div>
                            </div>

                            {message && <p className="form-message">{message}</p>}

                            <div className="modal-actions-row">
                                {userBet && (
                                    <button
                                        type="button"
                                        className="sheet-btn cancel"
                                        onClick={handleCancelBet}
                                        disabled={isLoading}
                                    >
                                        Отменить
                                    </button>
                                )}
                                <button type="submit" className="sheet-btn submit" disabled={isLoading}>
                                    {isLoading ? '...' : (userBet ? 'Добавить' : 'Поставить')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}